import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tab,
  Tabs,
  Alert,
  Fade,
  Zoom,
  useTheme,
  Collapse,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DocumentIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import ArticleIcon from '@mui/icons-material/Article';
import BarChartIcon from '@mui/icons-material/BarChart';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SettingsIcon from '@mui/icons-material/Settings';
import FolderIcon from '@mui/icons-material/Folder';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MarkdownRenderer from '../Common/MarkdownRenderer';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';

// Custom component for displaying the answer
const AnswerDisplay = ({ answer }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={2}
      sx={{
        p: 4,
        mb: 3,
        borderRadius: 3,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.5)' : 'rgba(248, 249, 250, 0.8)',
        backdropFilter: 'blur(8px)',
        border: `1px solid ${theme.palette.divider}`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '4px',
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            mr: 2,
            width: 36,
            height: 36,
          }}
        >
          <AutoAwesomeIcon fontSize="small" />
        </Avatar>
        <Typography variant="h6" fontWeight="bold">
          Answer
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box
        sx={{
          typography: 'body1',
          '& p': {
            mb: 2,
            lineHeight: 1.7,
          },
          '& ul, & ol': {
            pl: 2,
            mb: 2,
          },
          '& li': {
            mb: 1,
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            mt: 3,
            mb: 2,
            fontWeight: 'bold',
          },
          '& code': {
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontFamily: 'monospace',
          },
          '& blockquote': {
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            pl: 2,
            py: 1,
            my: 2,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          },
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        }}
      >
        <MarkdownRenderer content={answer} variant="body1" />
      </Box>
    </Paper>
  );
};

// Custom component for displaying retrieved chunks
const ChunksList = ({ chunks = [] }) => {
  const theme = useTheme();
  const [expandedPanel, setExpandedPanel] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : null);
  };

  const getScoreColor = (score) => {
    if (!score) return 'default';
    if (score > 0.8) return 'success';
    if (score > 0.6) return 'primary';
    if (score > 0.4) return 'info';
    if (score > 0.2) return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Avatar
          sx={{
            bgcolor: 'info.main',
            mr: 2,
            width: 36,
            height: 36,
          }}
        >
          <ArticleIcon fontSize="small" />
        </Avatar>
        <Typography variant="h6" fontWeight="bold">
          Retrieved Chunks
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {!chunks || chunks.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No chunks were retrieved for this query.
        </Alert>
      ) : (
        chunks.map((chunk, index) => (
          <Accordion
            key={`chunk-${index}`}
            expanded={expandedPanel === `chunk-${index}`}
            onChange={handleAccordionChange(`chunk-${index}`)}
            sx={{
              mb: 2,
              borderRadius: '12px !important',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: expandedPanel === `chunk-${index}` ? 'primary.light' : theme.palette.divider,
              boxShadow: expandedPanel === `chunk-${index}` ? 2 : 0,
              transition: 'all 0.3s ease',
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 64,
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0',
                },
                bgcolor: expandedPanel === `chunk-${index}` ? 'rgba(63, 81, 181, 0.05)' : 'transparent',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    sx={{
                      bgcolor: getScoreColor(chunk.score) + '.main',
                      mr: 2,
                      width: 32,
                      height: 32,
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {index + 1}
                  </Avatar>
                  <Box>
                    <Typography fontWeight={500}>
                      {chunk.metadata.document_id || 'Unknown Document'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {chunk.metadata.topic_id || 'Unknown Topic'}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={`Score: ${chunk.score ? chunk.score.toFixed(2) : 'N/A'}`}
                  color={getScoreColor(chunk.score)}
                  size="small"
                  sx={{
                    fontWeight: 'bold',
                    borderRadius: 6,
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3, pt: 1 }}>
              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.01)',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  }}
                >
                  {chunk.text_chunk}
                </Typography>
              </Card>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  icon={<FolderIcon fontSize="small" />}
                  label={`Topic: ${chunk.metadata.topic_id || 'Unknown'}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ borderRadius: 6 }}
                />
                {chunk.metadata.page_number && (
                  <Chip
                    label={`Page: ${chunk.metadata.page_number}`}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: 6 }}
                  />
                )}
                {chunk.metadata.chunk_id && (
                  <Chip
                    label={`Chunk ID: ${chunk.metadata.chunk_id}`}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: 6 }}
                  />
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

// Custom component for displaying reasoning logs
const ReasoningLogs = ({ logs }) => {
  const theme = useTheme();
  const [expandedLog, setExpandedLog] = useState(null);

  const handleExpandLog = (logIndex) => {
    setExpandedLog(expandedLog === logIndex ? null : logIndex);
  };

  if (!logs || logs.length === 0) {
    return (
      <Alert
        severity="info"
        sx={{
          mt: 2,
          borderRadius: 2,
        }}
      >
        No reasoning logs available.
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Avatar
          sx={{
            bgcolor: 'warning.main',
            mr: 2,
            width: 36,
            height: 36,
          }}
        >
          <PsychologyIcon fontSize="small" />
        </Avatar>
        <Typography variant="h6" fontWeight="bold">
          Reasoning Process
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {logs.map((log, index) => (
          <Card
            key={index}
            elevation={expandedLog === index ? 3 : 1}
            sx={{
              borderRadius: 3,
              transition: 'all 0.3s ease',
              border: '1px solid',
              borderColor: expandedLog === index ? 'primary.light' : theme.palette.divider,
              overflow: 'hidden',
            }}
          >
            <CardHeader
              avatar={
                <Avatar
                  sx={{
                    bgcolor: index === logs.length - 1 ? 'success.main' : 'primary.main',
                    boxShadow: 1,
                  }}
                >
                  {log.attempt + 1}
                </Avatar>
              }
              title={
                <Typography variant="subtitle1" fontWeight="bold">
                  {index === logs.length - 1 ? 'Final Attempt' : `Attempt ${log.attempt + 1}`}
                </Typography>
              }
              subheader={
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    size="small"
                    label={log.query}
                    sx={{
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      borderRadius: 6,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: theme.palette.divider,
                      '& .MuiChip-label': {
                        px: 1,
                      },
                    }}
                  />
                </Box>
              }
              action={
                <IconButton
                  onClick={() => handleExpandLog(index)}
                  sx={{
                    transform: expandedLog === index ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              }
              sx={{
                bgcolor: expandedLog === index ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                borderBottom: expandedLog === index ? `1px solid ${theme.palette.divider}` : 'none',
                transition: 'all 0.3s ease',
              }}
            />

            <Collapse in={expandedLog === index} timeout="auto" unmountOnExit>
              <CardContent>
                {log.evaluation && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Chip
                        label="Evaluation"
                        color="primary"
                        size="small"
                        sx={{
                          borderRadius: 6,
                          mr: 1,
                        }}
                      />
                      <Chip
                        label={log.evaluation.is_sufficient ? 'Sufficient' : 'Insufficient'}
                        color={log.evaluation.is_sufficient ? 'success' : 'warning'}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: 6,
                        }}
                      />
                    </Box>

                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 2,
                        mt: 1,
                        borderRadius: 2,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.01)',
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {log.evaluation.reasoning}
                      </Typography>
                    </Card>

                    {log.evaluation.missing_info_or_next_questions && log.evaluation.missing_info_or_next_questions.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Missing Information / Follow-up Questions:
                        </Typography>
                        <List
                          dense
                          sx={{
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.01)',
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          {log.evaluation.missing_info_or_next_questions.map((item, i) => (
                            <ListItem key={i} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Avatar
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    bgcolor: 'info.main',
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {i+1}
                                </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="body2">
                                    {item}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Box>
                )}

                {log.action && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Action:
                    </Typography>
                    <Chip
                      label={log.action}
                      color="secondary"
                      variant="outlined"
                      size="small"
                      sx={{
                        borderRadius: 6,
                      }}
                    />
                  </Box>
                )}

                {log.final_note && (
                  <Alert
                    severity="info"
                    variant="outlined"
                    icon={<InfoIcon />}
                    sx={{
                      mt: 2,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" fontWeight="medium">
                      {log.final_note}
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Collapse>
          </Card>
        ))}
      </Box>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ExpandMoreIcon />}
          onClick={() => {
            if (expandedLog === null) {
              setExpandedLog(logs.length - 1); // Expand the last log
            } else {
              setExpandedLog(null); // Collapse all
            }
          }}
          sx={{
            borderRadius: 8,
            px: 3,
          }}
        >
          {expandedLog === null ? 'Expand Final Attempt' : 'Collapse All'}
        </Button>
      </Box>
    </Box>
  );
};

// Score visualization component
const ScoreVisualization = ({ chunks }) => {
  const theme = useTheme();

  if (!chunks || chunks.length === 0) {
    return (
      <Alert
        severity="info"
        sx={{
          borderRadius: 2,
          mb: 3,
        }}
      >
        No chunks available for visualization.
      </Alert>
    );
  }

  const data = chunks.map((chunk, index) => ({
    name: `Chunk ${index + 1}`,
    score: chunk.score ? parseFloat((chunk.score * 100).toFixed(1)) : 0,
    document: chunk.metadata.document_id || 'Unknown',
    topic: chunk.metadata.topic_id || 'Unknown',
  }));

  // Custom colors for the bars based on score
  const getBarColor = (score) => {
    if (score > 80) return theme.palette.success.main;
    if (score > 60) return theme.palette.primary.main;
    if (score > 40) return theme.palette.info.main;
    if (score > 20) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Avatar
          sx={{
            bgcolor: 'secondary.main',
            mr: 2,
            width: 36,
            height: 36,
          }}
        >
          <BarChartIcon fontSize="small" />
        </Avatar>
        <Typography variant="h6" fontWeight="bold">
          Relevance Scores
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Card
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 3,
          height: 400,
          boxShadow: 'inset 0 0 10px rgba(0,0,0,0.03)',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 60,
            }}
            barSize={40}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke={theme.palette.divider}
            />
            <XAxis
              dataKey="name"
              tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              tickLine={{ stroke: theme.palette.divider }}
              axisLine={{ stroke: theme.palette.divider }}
              dy={10}
            />
            <YAxis
              label={{
                value: 'Relevance Score (%)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  textAnchor: 'middle',
                  fill: theme.palette.text.secondary,
                  fontSize: 12,
                },
                dy: -10,
              }}
              tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              tickLine={{ stroke: theme.palette.divider }}
              axisLine={{ stroke: theme.palette.divider }}
              domain={[0, 100]}
            />
            <RechartsTooltip
              cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
              contentStyle={{
                borderRadius: 8,
                border: 'none',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                padding: '10px 14px',
                backgroundColor: theme.palette.background.paper,
              }}
              formatter={(value, name, props) => {
                return [
                  <span style={{ color: getBarColor(value), fontWeight: 'bold' }}>{value}%</span>,
                  'Relevance Score'
                ];
              }}
              labelFormatter={(label) => (
                <span style={{ fontWeight: 'bold', color: theme.palette.text.primary }}>{label}</span>
              )}
            />
            <Bar
              dataKey="score"
              radius={[4, 4, 0, 0]}
              label={{
                position: 'top',
                fill: theme.palette.text.secondary,
                fontSize: 12,
                formatter: (value) => `${value}%`,
              }}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getBarColor(entry.score)}
                  style={{
                    filter: 'drop-shadow(0px 2px 3px rgba(0,0,0,0.1))',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      filter: 'drop-shadow(0px 4px 6px rgba(0,0,0,0.2))',
                      opacity: 0.9,
                    },
                  }}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Document Information
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {data.map((item, index) => (
            <Chip
              key={index}
              icon={<DocumentIcon fontSize="small" />}
              label={`Chunk ${index + 1}: ${item.document} (${item.topic})`}
              variant="outlined"
              size="small"
              sx={{
                borderRadius: 6,
                borderColor: getBarColor(item.score),
                '& .MuiChip-icon': {
                  color: getBarColor(item.score),
                },
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// Main QueryResults component
const QueryResults = ({ results, showReasoning }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [mounted, setMounted] = useState(false);

  // Al inicio del componente, asegúrate de extraer correctamente los chunks
  const chunks = results ? 
    (showReasoning ? results.final_chunks : results.retrieved_chunks) : 
    [];
    
  useEffect(() => {
    if (results) {
      setMounted(true);
    }
  }, [results]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (!results) {
    return null;
  }

  return (
    <Fade in={mounted} timeout={800}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative background element */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -80,
            left: -80,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.palette.secondary.light}10, ${theme.palette.secondary.main}05)`,
            zIndex: 0,
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Avatar
              sx={{
                bgcolor: 'secondary.main',
                mr: 2,
                width: 48,
                height: 48,
                boxShadow: 2,
              }}
            >
              <AutoAwesomeIcon />
            </Avatar>
            <Typography variant="h5" component="h2" fontWeight="bold">
              Results
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 9 }}>
            Answer generated using {results.rag_strategy_used} strategy
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              icon={<SettingsIcon />}
              label={`Strategy: ${results.rag_strategy_used}`}
              color="primary"
              variant="outlined"
              sx={{
                borderRadius: 8,
                py: 2,
                px: 1,
                '& .MuiChip-label': { px: 1 },
              }}
            />
            {results.topic_ids_searched && results.topic_ids_searched.length > 0 ? (
              <Chip
                icon={<FolderIcon />}
                label={`Topics: ${results.topic_ids_searched.join(', ')}`}
                color="secondary"
                variant="outlined"
                sx={{
                  borderRadius: 8,
                  py: 2,
                  px: 1,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            ) : (
              <Chip
                icon={<FolderIcon />}
                label="All topics searched"
                color="secondary"
                variant="outlined"
                sx={{
                  borderRadius: 8,
                  py: 2,
                  px: 1,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}
            {showReasoning && (
              <Chip
                icon={<PsychologyIcon />}
                label="Reasoning loop enabled"
                color="info"
                variant="outlined"
                sx={{
                  borderRadius: 8,
                  py: 2,
                  px: 1,
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}
          </Box>

          <Zoom in={true} style={{ transitionDelay: '200ms' }}>
            <Box>
              <AnswerDisplay answer={results.answer} />
            </Box>
          </Zoom>

          <Box sx={{ mt: 4 }}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                mb: 3,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '1px',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  opacity: 0.5,
                },
              }}
            >
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="results tabs"
                textColor="primary"
                indicatorColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    minWidth: 'auto',
                    px: 3,
                    py: 2,
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: 'primary.main',
                      opacity: 1,
                    },
                  },
                  '& .Mui-selected': {
                    fontWeight: 600,
                  },
                }}
              >
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ArticleIcon sx={{ mr: 1 }} />
                      <span>Retrieved Chunks</span>
                    </Box>
                  }
                  id="tab-0"
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BarChartIcon sx={{ mr: 1 }} />
                      <span>Visualization</span>
                    </Box>
                  }
                  id="tab-1"
                />
                {showReasoning && (
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PsychologyIcon sx={{ mr: 1 }} />
                        <span>Reasoning Process</span>
                      </Box>
                    }
                    id="tab-2"
                  />
                )}
              </Tabs>
            </Box>

            <Box role="tabpanel" hidden={tabValue !== 0}>
              {tabValue === 0 && (
                <Fade in={true} timeout={500}>
                  <Box>
                    <ChunksList
                      chunks={showReasoning ? results.final_chunks : results.retrieved_chunks}
                    />
                  </Box>
                </Fade>
              )}
            </Box>

            <Box role="tabpanel" hidden={tabValue !== 1}>
              {tabValue === 1 && (
                <Fade in={true} timeout={500}>
                  <Box>
                    <ScoreVisualization
                      chunks={showReasoning ? results.final_chunks : results.retrieved_chunks}
                    />
                  </Box>
                </Fade>
              )}
            </Box>

            {showReasoning && (
              <Box role="tabpanel" hidden={tabValue !== 2}>
                {tabValue === 2 && (
                  <Fade in={true} timeout={500}>
                    <Box>
                      <ReasoningLogs logs={results.reasoning_log} />
                    </Box>
                  </Fade>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </Fade>
  );
};

export default QueryResults;



